import React, {
  memo,
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { Icon } from 'components';
import Tooltip from 'components/Tooltip';
import { DropdownV3, InputV2 } from 'components/ComponentV2';
import MultiSelect from 'components/MultiSelect';
import useScrollOffset from 'hooks/useScrollOffset';
import { DemographicsInfo } from 'hooks/useCandidate';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useCommonList } from 'lib/contexts/common-list-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { parseArrayResponse } from 'lib/utils/parser';
import { Footer } from '../Footer/Footer.component';

import styles from '../Onboarding.module.scss';

export interface DemographicsProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  onNextClick: ({
    candidateInfo,
    isBackClicked,
  }: {
    candidateInfo: DemographicsInfo;
    isBackClicked?: boolean;
  }) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  isSimplifiedVersion?: boolean;
}

export const Demographics: React.FC<DemographicsProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    onNextClick,
    isLoading,
    isSimplifiedVersion = false,
    isBackLoading,
  }: DemographicsProps) => {
    const [tCommon] = useTranslation('common');
    const [t] = useTranslation('candidate-onboarding');
    const bottomRef = useRef<HTMLDivElement>(null);

    const { candidate } = useAuth();
    const contentClassNames = cn(styles.container, className);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const physicalDisabilityDropdownRef = useRef<HTMLButtonElement>(null);
    const congnitiveDisabilityDropdownRef = useRef<HTMLButtonElement>(null);
    const socioEconomicStatusDropdownRef = useRef<HTMLButtonElement>(null);
    const ethnicityDropdownRef = useRef<HTMLDivElement>(null);
    const genderDropdownRef = useRef<HTMLButtonElement>(null);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    const { getScrollOffset, isWindowScrolled } = useScrollOffset();

    const {
      genders,
      raceEthnicity,
      physicalDisability,
      congnitiveDisability,
      socioEconomicStatus,
    } = useCommonList();

    const [candidateInfo, setCandidateInfo] = useState<DemographicsInfo>({
      gender: '',
      ethnicity_ids: [],
      physical_disability: '',
      congnitive_disability: '',
      socio_economic_status: '',
    });

    useEffect(() => {
      if (!candidate) return;

      const {
        gender,
        ethnicities: ethinicitiesResponse,
        physical_disability,
        congnitive_disability,
        socio_economic_status,
      } = candidate || {};

      const ethnicities = parseArrayResponse(ethinicitiesResponse);

      setCandidateInfo((info) => ({
        ...info,
        gender,
        ethnicity_ids: ethnicities.map((item) => item.id),
        physical_disability,
        congnitive_disability,
        socio_economic_status,
      }));
    }, [candidate]);

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const onSubmit = (isBackClicked?: boolean) => {
      onNextClick({ candidateInfo, isBackClicked });
    };

    const isReady = isSimplifiedVersion
      ? !!(candidateInfo?.first_name && candidateInfo?.last_name)
      : true;

    const scrollToDropdown = (dropdownRef: React.RefObject<HTMLElement>) => {
      const topSpace = 12;
      const offset = getScrollOffset(
        dropdownRef,
        containerRef,
        headerRef,
        topSpace
      );
      window.scrollTo({ top: offset, behavior: 'smooth' });
    };

    const scrollToDropdownPhysical = () => {
      scrollToDropdown(physicalDisabilityDropdownRef);
    };

    const scrollToDropdownSocioEconomic = () => {
      scrollToDropdown(socioEconomicStatusDropdownRef);
    };
    const scrollToDropdownEthnicity = () => {
      scrollToDropdown(ethnicityDropdownRef);
    };
    const scrollToDropdownCongnitive = () => {
      scrollToDropdown(congnitiveDisabilityDropdownRef);
    };

    const scrollToDropdownGender = () => {
      scrollToDropdown(genderDropdownRef);
    };

    const onRaceAndEthnicitySelect = useCallback(
      (values: Array<string>) => {
        setCandidateInfo((info) => ({
          ...info,
          ethnicity_ids: values,
        }));
      },
      [setCandidateInfo]
    );

    const selectedEthnicityText = useMemo(() => {
      const labelsList = raceEthnicity
        ?.filter((item) => !!candidateInfo?.ethnicity_ids?.includes(item.id))
        .map((item) => item.label);
      return (
        labelsList?.join(', ') ||
        t('education-demographic-detail.select-ethnicity')
      );
    }, [candidateInfo]);

    const onPhysicalDisabilitySelect = (value: string) => {
      if (!physicalDisability) return;
      const disability = physicalDisability.find(
        (disability) => disability.value === value
      );
      if (disability) {
        setCandidateInfo((info) => ({
          ...info,
          physical_disability: disability.label,
        }));
      }
    };

    const onCongnitiveDisabilitySelect = (value: string) => {
      if (!congnitiveDisability) return;
      const disability = congnitiveDisability.find(
        (disability) => disability.value === value
      );
      if (disability) {
        setCandidateInfo((info) => ({
          ...info,
          congnitive_disability: disability.label,
        }));
      }
    };

    const onSocioEconomicStatusSelect = (value: string) => {
      if (!socioEconomicStatus) return;
      const economicStatus = socioEconomicStatus.find(
        (economicStatus) => economicStatus.value === value
      );
      if (economicStatus) {
        setCandidateInfo((info) => ({
          ...info,
          socio_economic_status: economicStatus.label,
        }));
      }
    };

    const onGenderSelect = (value: string) => {
      if (!genders) return;
      const gender = genders.find((gender) => gender.label === value);
      if (gender) {
        setCandidateInfo((prevInfo) => ({
          ...prevInfo,
          gender: gender.label,
        }));
      }
    };

    const getTooltipContent = (content: string) => {
      return (
        <Tooltip
          content={content}
          position="top"
          xAlign="center"
          color="dark"
          className={cn(styles.tooltipContainer)}
          containerClassName={styles.tooltipContainerContent}
        >
          <Icon
            iconName="icon_info"
            className={styles.tooltipContainerIcon}
            size="small"
          />
        </Tooltip>
      );
    };

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="Demographics"
        ref={containerRef}
      >
        <div className={styles.content}>
          <p ref={headerRef} className={styles.setup}>
            {isSimplifiedVersion
              ? t('education-demographic-detail.basic-info')
              : t('education-demographic-detail.demographic-page-title')}
            {!isSimplifiedVersion && (
              <span className={styles.optionalText}>
                {' '}
                ({t('education-demographic-detail.optional')})
              </span>
            )}
          </p>
          {isSimplifiedVersion && (
            <div className={styles.section}>
              <div className={styles.nameContainer}>
                <InputV2
                  maxLength={120}
                  label={t('education-demographic-detail.first-name')}
                  placeholder={t('education-demographic-detail.add-first-name')}
                  value={candidateInfo?.first_name}
                  onChange={(event) => {
                    const first_name = event.target.value;
                    setCandidateInfo((candidateInfo) => ({
                      ...candidateInfo,
                      first_name,
                    }));
                  }}
                  className={styles.input}
                  labelClassName={styles.inputLabel}
                />
                <InputV2
                  maxLength={120}
                  label={t('education-demographic-detail.last-name')}
                  placeholder={t('education-demographic-detail.add-last-name')}
                  value={candidateInfo?.last_name}
                  onChange={(event) => {
                    const last_name = event.target.value;

                    setCandidateInfo((candidateInfo) => ({
                      ...candidateInfo,
                      last_name,
                    }));
                  }}
                  className={styles.input}
                  labelClassName={styles.inputLabel}
                />
              </div>
            </div>
          )}
          <div className={styles.section}>
            <div className={styles.sectionContainer}>
              <DropdownV3
                label={t('education-demographic-detail.select-gender')}
                title={t('education-demographic-detail.gender')}
                value={candidateInfo?.gender || ''}
                onChange={(value) => onGenderSelect(value)}
                options={genders || []}
                isSearchable={false}
                selectedValueLabel={candidateInfo?.gender || ''}
                isValid={true}
                triggerButtonClassName={styles.trigger}
                dropdownClassName={styles.dropdown3}
                selectedValueClassName={styles.selectedValue}
                scrollToDropdown={scrollToDropdownGender}
                ref={genderDropdownRef}
                labelClassName={styles.questionText}
                showOptionalLabel={isSimplifiedVersion}
              />
              <div className={styles.ethnicityContainer}>
                <div ref={ethnicityDropdownRef}>
                  <div className={styles.MultiselectLabel}>
                    <span className={styles.questionText}>
                      {t('education-demographic-detail.ethnicity')}
                    </span>
                    {isSimplifiedVersion && (
                      <p className={styles.optional}>({tCommon('optional')})</p>
                    )}
                  </div>
                </div>
                <div>
                  <MultiSelect
                    title={
                      candidateInfo?.ethnicity_ids?.length === 0
                        ? t('education-demographic-detail.select-ethnicity')
                        : selectedEthnicityText
                    }
                    placeholder="Search"
                    options={raceEthnicity}
                    values={candidateInfo?.ethnicity_ids || []}
                    onChange={onRaceAndEthnicitySelect}
                    className={cn(styles.dropdown)}
                    dropdownClassName={styles.multiDropdown}
                    checkBoxClassName={styles.checkbox}
                    showCount={false}
                    isSearchable
                    fullWidth
                    iconClassName="icon_arrow-down"
                    position="bottom"
                    buttonTitleClassName={styles.multiDropdownTrigger}
                    scrollToDropdown={scrollToDropdownEthnicity}
                    triggerClassName={styles.multiselectButton}
                  />
                </div>
              </div>
            </div>
            <DropdownV3
              label={tCommon('select')}
              title={t('education-demographic-detail.physical-disability')}
              value={candidateInfo?.physical_disability || ''}
              onChange={(value) => onPhysicalDisabilitySelect(value)}
              options={physicalDisability || []}
              isSearchable={false}
              selectedValueLabel={candidateInfo?.physical_disability || ''}
              isValid={true}
              className={styles.question}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={styles.dropdown3}
              labelClassName={styles.questionText}
              infoToolTip={getTooltipContent(
                t('education-demographic-detail.physical-disability-tooltip')
              )}
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownPhysical}
              ref={physicalDisabilityDropdownRef}
              showOptionalLabel={isSimplifiedVersion}
            />
            <DropdownV3
              label={tCommon('select')}
              title={t('education-demographic-detail.cognitive-disability')}
              value={candidateInfo?.congnitive_disability || ''}
              onChange={(value) => onCongnitiveDisabilitySelect(value)}
              options={congnitiveDisability || []}
              isSearchable={false}
              selectedValueLabel={candidateInfo?.congnitive_disability || ''}
              isValid={true}
              className={styles.question}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={styles.dropdown3}
              labelClassName={styles.questionText}
              infoToolTip={getTooltipContent(
                t('education-demographic-detail.cognitive-disability-tooltip')
              )}
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownCongnitive}
              ref={congnitiveDisabilityDropdownRef}
              showOptionalLabel={isSimplifiedVersion}
            />
            <DropdownV3
              label={tCommon('select')}
              title={t(
                'education-demographic-detail.socio-economic-background'
              )}
              value={candidateInfo?.socio_economic_status || ''}
              onChange={(value) => onSocioEconomicStatusSelect(value)}
              options={socioEconomicStatus || []}
              isSearchable={false}
              selectedValueLabel={candidateInfo?.socio_economic_status || ''}
              isValid={true}
              className={styles.question}
              triggerButtonClassName={styles.trigger}
              dropdownClassName={cn(styles.dropdown3, styles.lastDropdown)}
              labelClassName={styles.questionText}
              infoToolTip={getTooltipContent(
                t(
                  'education-demographic-detail.socio-economic-background-tooltip'
                )
              )}
              selectedValueClassName={styles.selectedValue}
              scrollToDropdown={scrollToDropdownSocioEconomic}
              ref={socioEconomicStatusDropdownRef}
              showOptionalLabel={isSimplifiedVersion}
            />
          </div>
          <div className={styles.notificationStubContainer}>
            <Icon
              iconName="icon_info"
              className={styles.iconInfo}
              size="auto"
            />
            <div>
              <p className={styles.notificationHeading}>
                {t('education-demographic-detail.notification-stub-heading')}
              </p>
              <p className={styles.notificationDesc}>
                {t(
                  'education-demographic-detail.notification-stub-description-1'
                )}
              </p>
              <br />
              <p className={styles.notificationDesc}>
                {t(
                  'education-demographic-detail.notification-stub-description-2'
                )}
              </p>
            </div>
          </div>
          <div className={styles['bottom-ref']} ref={bottomRef}></div>
        </div>

        <Footer
          isBackLoading={isBackLoading}
          isLoading={isLoading}
          onSubmit={onSubmit}
          primaryCtaText={tCommon('get-started')}
          isFullyScrolled={fullyScrolled}
          isWindowScrolled={isWindowScrolled}
          isReady={isReady}
        />
      </div>
    );
  }
);

Demographics.displayName = 'Demographics';
